import { create } from "zustand";

/**
 * ONE "a portal link is being minted for this guest" lock per guest, shared by
 * every button in the popup that mints one: the header action (Actions.tsx) and
 * the layout-only portal-link cell (W3-3). Each used to be — and the header
 * still would be — guarded by a ref of its own, and two refs cannot see each
 * other: header + cell clicked in one tick minted two tokens (two rows in
 * "Shared links", two valid links in circulation).
 *
 * The lock is a TOKEN, not a flag: whoever acquired it is the only one who can
 * release it. A popup closed mid-mint releases on unmount (as the old ref died
 * with its component), and the late answer of that mint must not then release
 * the lock a NEW mint has taken in the meantime.
 *
 * A store rather than a module-level Set so a button can show the mint the
 * OTHER button started (its click is ignored while the lock is held — a
 * spinner says why).
 */
type PortalLinkMintLockState = {
  /** lock key → token of the holder. */
  holders: Record<string, number>;
};

export const usePortalLinkMintLock = create<PortalLinkMintLockState>(() => ({
  holders: {},
}));

let lastToken = 0;

export function portalLinkLockKey(eventId: string, guestId: string): string {
  return `${eventId}:${guestId}`;
}

/** The lock's token, or `null` when somebody holds it already. Synchronous —
 *  two clicks inside one tick see each other. */
export function acquirePortalLinkMint(key: string): number | null {
  const { holders } = usePortalLinkMintLock.getState();
  if (holders[key] !== undefined) return null;
  const token = ++lastToken;
  usePortalLinkMintLock.setState({ holders: { ...holders, [key]: token } });
  return token;
}

/** Releases the lock — only for the token that holds it. */
export function releasePortalLinkMint(key: string, token: number): void {
  const { holders } = usePortalLinkMintLock.getState();
  if (holders[key] !== token) return;
  const rest = { ...holders };
  delete rest[key];
  usePortalLinkMintLock.setState({ holders: rest });
}
